<template>
    <div :class="['sales-filters', { dark: theme === THEMES.DARK }]">
        <div class="filters-container">
            <div class="filter-group">
                <input 
                    type="text" 
                    v-model="searchValue" 
                    @input="onSearchChange"
                    placeholder="Search by invoice or customer…"
                    aria-label="Search sales by invoice or customer"
                    autocomplete="off"
                    class="filter-input search-input"
                />
            </div>
            
            <div class="filter-group">
                <select 
                    v-model="locationValue" 
                    @change="onLocationChange"
                    aria-label="Filter sales by location"
                    class="filter-input select-input"
                >
                    <option value="">📍 All Locations</option>
                    <option 
                        v-for="warehouse in warehouses" 
                        :key="warehouse.id" 
                        :value="warehouse.id"
                    >
                        {{ warehouse.title || warehouse.name }}
                    </option>
                </select>
            </div>
            
            <div class="filter-group">
                <select 
                    v-model="selectedTimeFrame" 
                    @change="onTimeFrameChange"
                    aria-label="Filter sales by time period"
                    class="filter-input select-input timeframe-select"
                >
                    <option value="">📅 Time Period</option>
                    <option 
                        v-for="option in timeFrameOptions" 
                        :key="option.value" 
                        :value="option.value"
                    >
                        {{ option.label }}
                    </option>
                </select>
            </div>
            
            <!-- Custom Date Range Inputs (shown when custom is selected) -->
            <div v-if="selectedTimeFrame === 'custom'" class="filter-group custom-date-group">
                <input 
                    type="date" 
                    v-model="customStartDate" 
                    @change="updateCustomRange"
                    class="filter-input date-input"
                    aria-label="Custom range start date"
                    :max="customEndDate"
                />
                <span class="date-separator">to</span>
                <input 
                    type="date" 
                    v-model="customEndDate" 
                    @change="updateCustomRange"
                    class="filter-input date-input"
                    aria-label="Custom range end date"
                    :min="customStartDate"
                />
            </div>
            
            <div class="filter-actions">
                <label v-if="allowHistoricalMode" class="historical-mode-toggle">
                    <input type="checkbox" v-model="includePilotValue" @change="onHistoricalModeChange" />
                    <span>Include pilot/test sales</span>
                </label>
                <button 
                    class="clear-btn" 
                    @click="clearFilters"
                    title="Clear all filters"
                >
                    🗑️ Clear
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex"
import { businessToday, getBusinessDateRangeForPreset, isBusinessDate } from "../../utils/business-date"

function getDefaultTimeFrame() {
    const range = getBusinessDateRangeForPreset("last7days")

    return {
        startDate: range.start,
        endDate: range.end,
        period: 'last7days'
    }
}

export default {
    name: "SalesFilters",
    props: {
        search: { type: String, default: "" },
        location: { type: String, default: "" },
        timeFrame: { 
            type: Object, 
            default: getDefaultTimeFrame
        },
        includePilotSales: { type: Boolean, default: false },
        allowHistoricalMode: { type: Boolean, default: false }
    },
    data() {
        return {
            searchValue: this.search,
            locationValue: this.location,
            includePilotValue: this.includePilotSales,
            searchTimeout: null,
            selectedTimeFrame: this.timeFrame.period || 'last7days',
            customStartDate: this.timeFrame.startDate || businessToday(),
            customEndDate: this.timeFrame.endDate || businessToday(),
            timeFrameOptions: [
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'last7days', label: 'Last 7 Days' },
                { value: 'last30days', label: 'Last 30 Days' },
                { value: 'last90days', label: 'Last 90 Days' },
                { value: 'last180days', label: 'Last 180 Days' },
                { value: 'thisMonth', label: 'This Month' },
                { value: 'lastMonth', label: 'Last Month' },
                { value: 'last3months', label: 'Last 3 Months' },
                { value: 'last6months', label: 'Last 6 Months' },
                { value: 'thisYear', label: 'This Year' },
                { value: 'lastYear', label: 'Last Year' },
                { value: 'rollingYear', label: 'Rolling Year' },
                { value: 'custom', label: 'Custom Range' }
            ]
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES", "warehouses"])
    },
    watch: {
        search(newVal) {
            this.searchValue = newVal
        },
        location(newVal) {
            this.locationValue = newVal
        },
        includePilotSales(newVal) {
            this.includePilotValue = newVal
        },
        timeFrame: {
            handler(newVal) {
                if (newVal) {
                    this.selectedTimeFrame = newVal.period || 'last7days'
                    this.customStartDate = newVal.startDate || businessToday()
                    this.customEndDate = newVal.endDate || businessToday()
                }
            },
            deep: true
        }
    },
    methods: {
        onSearchChange() {
            // Debounce search input
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.$emit('update:search', this.searchValue)
                this.emitChange()
            }, 300)
        },
        
        onLocationChange() {
            this.$emit('update:location', this.locationValue)
            this.emitChange()
        },
        onHistoricalModeChange() {
            this.$emit('update:includePilotSales', this.includePilotValue)
            this.emitChange()
        },
        
        onTimeFrameChange() {
            this.calculateDateRange()
        },
        
        calculateDateRange() {
            const range = this.selectedTimeFrame === 'custom'
                ? { start: this.customStartDate, end: this.customEndDate }
                : getBusinessDateRangeForPreset(this.selectedTimeFrame)

            if (!isBusinessDate(range.start) || !isBusinessDate(range.end) || range.start > range.end) return

            this.customStartDate = range.start
            this.customEndDate = range.end
            
            const timeFrameData = {
                startDate: range.start,
                endDate: range.end,
                period: this.selectedTimeFrame
            }
            
            this.$emit('timeframe-change', timeFrameData)
            this.emitChange()
        },
        
        updateCustomRange() {
            if (this.selectedTimeFrame === 'custom') {
                const timeFrameData = {
                    startDate: this.customStartDate,
                    endDate: this.customEndDate,
                    period: 'custom'
                }
                
                this.$emit('timeframe-change', timeFrameData)
                this.emitChange()
            }
        },
        
        clearFilters() {
            this.searchValue = ""
            this.locationValue = ""
            this.includePilotValue = false
            this.selectedTimeFrame = "last7days"

            this.$emit('reset')
        },
        
        emitChange() {
            this.$emit('change', {
                search: this.searchValue,
                location: this.locationValue,
                includePilotSales: this.includePilotValue
            })
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.sales-filters {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    
    &.dark {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }
}

.filters-container {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
}

.filter-group {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
    
    @media (max-width: 768px) {
        flex: none;
    }
    
    &.custom-date-group {
        gap: 8px;
        flex: 2;
        min-width: 200px;
        
        @media (max-width: 768px) {
            flex-direction: column;
            gap: 8px;
        }
    }
}

.filter-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: $textColor;
    
    .dark & {
        color: $darkTextColor;
    }
    
    i {
        font-size: 16px;
        color: rgba($textColor, 0.6);
        .dark & { color: rgba($darkTextColor, 0.75); }
    }
}

.filter-input {
    padding: 8px 12px;
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
    font-size: 14px;
    background: $lightCardBg;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    min-width: 0;
    width: 100%;
    
    &:focus {
        outline: none;
        border-color: $primaryColor;
        box-shadow: 0 0 0 3px rgba($primaryColor, 0.1);
    }
    
    .dark & {
        background: $darkInputBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
        
        &:focus {
            border-color: $secondaryColorDark;
            box-shadow: 0 0 0 3px rgba($secondaryColorDark, 0.1);
        }
    }
}

.search-input {
    min-width: 200px;
    
    @media (max-width: 768px) {
        min-width: 100%;
    }
}

.select-input {
    min-width: 140px;
    cursor: pointer;
    
    &.timeframe-select {
        min-width: 160px;
        font-weight: 500;
        
        @media (max-width: 768px) {
            min-width: 100%;
        }
    }
    
    @media (max-width: 768px) {
        min-width: 100%;
    }
}

.date-input {
    min-width: 120px;
    font-size: 13px;
    
    @media (max-width: 768px) {
        min-width: 100%;
    }
}

.date-separator {
    font-size: 12px;
    color: rgba($textColor, 0.55);
    font-weight: 500;
    white-space: nowrap;
    margin: 0 4px;
    
    .dark & {
        color: rgba($darkTextColor, 0.7);
    }
    
    @media (max-width: 768px) {
        align-self: center;
        margin: 4px 0;
    }
}

.filter-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.clear-btn {
    padding: 8px 16px;
    background: $lightHoverColor;
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: $textColor;
    transition: background-color 0.2s ease, border-color 0.2s ease;
    white-space: nowrap;
    
    &:hover {
        background: lighten($lightHoverColor, 4%);
        border-color: lighten($lightBorderColor, 8%);
    }
    
    .dark & {
        background: $darkInputBg;
        border-color: $darkBorderColor;
        color: $darkTextColor;
        
        &:hover {
            background: lighten($darkCardBg, 6%);
            border-color: lighten($darkBorderColor, 8%);
        }
    }
}

@media (max-width: 968px) {
    .filters-container {
        gap: 12px;
    }
    
    .filter-group {
        min-width: calc(50% - 6px);
        
        &.custom-date-group {
            min-width: 100%;
        }
    }
}

@media (max-width: 576px) {
    .filter-group {
        min-width: 100%;
    }
}
</style> 
